import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, LinearProgress, Link, Stack, Typography,
} from '@mui/material';
import Panel from '../crt/Panel';
import SectionTitle from '../crt/SectionTitle';
import { patreonReleaseAPI } from '../../services/api';
import StagedMediaPlayer from './StagedMediaPlayer';
import {
  canDiscard, canRelease, canRetry, fmtBytes, fmtPreview, releaseSentence,
  PatreonReleasePackage, ReleasePart, StagedMediaLinks,
} from './patreonStagedRelease';

/**
 * Patreon releases staged by the REAPER exporter.
 *
 * The exporter uploads the files and fills in every setting, but it cannot
 * release: the API refuses its token on the release endpoint. Nothing exists
 * on Patreon until Release is pressed here, by someone logged in.
 */
const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  staged: 'warning', releasing: 'info', done: 'success', error: 'error',
};

const PartLine: React.FC<{ name: string; part: ReleasePart }> = ({ name, part }) => (
  <Box sx={{ mb: 1 }}>
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip size="small" label={name.toUpperCase()} />
      <Chip size="small" color={part.status === 'done' ? 'success' : part.status === 'error' ? 'error' : 'info'}
        label={part.status === 'running' ? (part.job_status || 'running') : part.status} />
      {part.patreon_url && (
        <Link href={part.patreon_url} target="_blank" rel="noreferrer">Open on Patreon</Link>
      )}
      {part.canonical_url && (
        <Link href={part.canonical_url} target="_blank" rel="noreferrer">Teaser page</Link>
      )}
    </Stack>
    {part.status === 'running' && (
      <LinearProgress variant={part.progress_pct ? 'determinate' : 'indeterminate'}
        value={part.progress_pct || 0} sx={{ mt: 0.5 }} />
    )}
    {part.error && <Alert severity="error" sx={{ mt: 0.5, py: 0 }}>{part.error}</Alert>}
  </Box>
);

const PatreonStagedReleases: React.FC = () => {
  const [packages, setPackages] = useState<PatreonReleasePackage[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ id: string; text: string } | null>(null);
  const timer = useRef<number | null>(null);
  // Which players are open ("<package_id>:<part>") and the links per package.
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [media, setMedia] = useState<Record<string, StagedMediaLinks>>({});
  const [mediaError, setMediaError] = useState<Record<string, string>>({});

  const toggle = async (id: string, part: string) => {
    const key = `${id}:${part}`;
    setOpen((o) => ({ ...o, [key]: !o[key] }));
    if (media[id] || open[key]) return;
    try {
      const links = await patreonReleaseAPI.media(id);
      setMedia((m) => ({ ...m, [id]: links }));
    } catch (e: any) {
      setMediaError((m) => ({ ...m, [id]: e?.response?.data?.detail || e?.message || String(e) }));
    }
  };

  const player = (id: string, part: 'audio' | 'video' | 'teaser', preview?: any) => {
    if (!open[`${id}:${part}`]) return null;
    if (mediaError[id]) return <Alert severity="error" sx={{ mt: 1, py: 0 }}>{mediaError[id]}</Alert>;
    const src = media[id]?.[part];
    if (!src) return <CircularProgress size={16} sx={{ mt: 1 }} />;
    return <StagedMediaPlayer kind={part === 'audio' ? 'audio' : 'video'} src={src} preview={preview} />;
  };

  const playButton = (id: string, part: 'audio' | 'video' | 'teaser') => (
    <Button size="small" variant="outlined" sx={{ mt: 0.5 }} onClick={() => toggle(id, part)}>
      {open[`${id}:${part}`] ? 'Hide player' : part === 'audio' ? 'Listen' : 'Watch'}
    </Button>
  );

  const load = useCallback(async () => {
    try {
      setPackages((await patreonReleaseAPI.list()).packages);
      setLoadError(null);
    } catch (e: any) {
      setLoadError(e?.response?.data?.detail || e?.message || String(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll only while something is mid-release.
  const releasing = !!packages?.some((p) => p.status === 'releasing');
  useEffect(() => {
    if (!releasing) return undefined;
    timer.current = window.setInterval(load, 5000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [releasing, load]);

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id); setActionError(null);
    try {
      await fn();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setActionError({ id, text: typeof d === 'string' ? d : d?.message || e?.message || String(e) });
    } finally {
      setBusy(null);
      load();
    }
  };

  return (
    <Panel label="PATREON :: STAGED RELEASES">
      <SectionTitle>{`> STAGED BY THE REAPER EXPORTER, RELEASED HERE_`}</SectionTitle>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button size="small" variant="outlined" onClick={load}>Refresh</Button>
          <Typography variant="body2" color="text.secondary">
            The exporter can upload and stage. Nothing is posted until you press Release.
          </Typography>
        </Stack>
        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
        {packages === null && !loadError && <CircularProgress size={20} />}
        {packages?.length === 0 && <Alert severity="info">Nothing is staged.</Alert>}

        {packages?.map((p) => (
          <Box key={p.package_id}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{p.release_key}</Typography>
              <Chip size="small" color={STATUS_COLOR[p.status] || 'default'} label={p.status.toUpperCase()} />
              {p.staged_count > 1 && p.status === 'staged' && (
                <Chip size="small" variant="outlined" label={`re-staged ${p.staged_count - 1}x`} />
              )}
              <Typography variant="caption" color="text.secondary">
                staged {p.updated_at ? new Date(p.updated_at).toLocaleString() : ''}
              </Typography>
            </Stack>

            {p.summary.posts.map((post) => (
              <Box key={post.part} sx={{ mb: 1.5, pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {post.part.toUpperCase()}: {post.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {post.file}{post.bytes ? ` · ${fmtBytes(post.bytes)}` : ''}
                </Typography>
                <Typography variant="caption" component="div">
                  <strong>Tiers:</strong> {post.tiers.join(', ') || 'none'}
                </Typography>
                <Typography variant="caption" component="div">
                  <strong>Collection:</strong> {post.collection || 'none'}
                  {post.part === 'audio' && <> · <strong>Podcast feed:</strong> {post.podcast || 'none'}</>}
                </Typography>
                <Typography variant="caption" component="div">
                  <strong>Preview:</strong> {fmtPreview(post.preview)}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {post.tags.map((t) => <Chip key={t} size="small" variant="outlined" label={t} />)}
                </Stack>
                {playButton(p.package_id, post.part)}
                {player(p.package_id, post.part, post.preview)}
              </Box>
            ))}

            {p.summary.teaser && (
              <Box sx={{ mb: 1.5, pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  PODCAST FEED TEASER: {p.summary.teaser.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {p.summary.teaser.file}{p.summary.teaser.bytes ? ` · ${fmtBytes(p.summary.teaser.bytes)}` : ''}
                  {p.summary.teaser.release_date
                    ? ` · dated ${new Date(p.summary.teaser.release_date).toLocaleString()}`
                    : ' · dated when you press Release'}
                </Typography>
                <Typography variant="caption" component="div">
                  Created as drafts (WordPress page and Megaphone episode). It still goes
                  public from the Publish tab, like any episode.
                </Typography>
                {playButton(p.package_id, 'teaser')}
                {player(p.package_id, 'teaser')}
              </Box>
            )}

            {p.description && (
              <Typography variant="body2" color="text.secondary"
                sx={{ whiteSpace: 'pre-wrap', mb: 1.5, maxHeight: 120, overflow: 'auto' }}>
                {p.description}
              </Typography>
            )}

            {Object.entries(p.parts).map(([name, part]) => (
              <PartLine key={name} name={name} part={part} />
            ))}

            {actionError?.id === p.package_id && (
              <Alert severity="error" sx={{ mb: 1 }}>{actionError.text}</Alert>
            )}

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {canRelease(p) && (
                <Button variant="contained" color="success" disabled={busy === p.package_id}
                  onClick={() => act(p.package_id, () => patreonReleaseAPI.release(p.package_id))}
                  startIcon={busy === p.package_id ? <CircularProgress size={16} /> : undefined}>
                  Release to Patreon
                </Button>
              )}
              {canRetry(p) && (
                <Button variant="contained" color="warning" disabled={busy === p.package_id}
                  onClick={() => act(p.package_id, () => patreonReleaseAPI.retry(p.package_id))}>
                  Retry failed parts
                </Button>
              )}
              {canDiscard(p) && (
                <Button variant="text" color="inherit" disabled={busy === p.package_id}
                  onClick={() => act(p.package_id, () => patreonReleaseAPI.discard(p.package_id))}>
                  Discard
                </Button>
              )}
              {canRelease(p) && (
                <Typography variant="caption" color="text.secondary">{releaseSentence(p)}</Typography>
              )}
            </Stack>
          </Box>
        ))}
      </Box>
    </Panel>
  );
};

export default PatreonStagedReleases;
